import { useState } from "react";
import { format } from "date-fns";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Panel } from "./panel";
import { cn } from "../../utils/common/cn";
import type { KGovernance } from "../../pages/PortfolioPerformance/lib/api/loss-calibration";

export function KGovernanceCard({
  governance,
  sandboxK,
  onSandboxKChange,
  onPromote,
  isPromoting,
  canPromote,
}: {
  governance: KGovernance;
  sandboxK: string;
  onSandboxKChange: (value: string) => void;
  onPromote: (k: number, note: string) => Promise<unknown> | void;
  isPromoting: boolean;
  /**
   * Contributor and above. Everyone who can open Loss Calibration may still edit
   * the sandbox k below -- that is a client-side what-if that changes nothing -- but
   * only a contributor may make it live.
   */
  canPromote: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  const parsed = Number(sandboxK);
  const validSandbox = sandboxK.trim() !== "" && Number.isFinite(parsed) && parsed > 0;
  const differs = validSandbox && parsed !== governance.liveK;

  return (
    <Panel
      className="mt-8"
      title="k Governance"
      description="k controls how much a segment's own experience counts before the tier pool takes over."
      right={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            disabled={sandboxK.trim() === ""}
            onClick={() => onSandboxKChange("")}
          >
            Clear Sandbox
          </Button>
          {canPromote && (
            <Button size="sm" className="h-9 text-xs" disabled={!differs} onClick={() => setOpen(true)}>
              Promote To Live
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-4 md:grid-cols-3">
        <div className="border border-border bg-secondary/40 p-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Live k
          </p>
          <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
            {governance.liveK}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Last estimated {format(new Date(governance.estimatedFrom.asOf), "d MMM yyyy")} from{" "}
            {governance.estimatedFrom.segments} segments
          </p>
        </div>

        <div
          className={cn(
            "border p-4",
            differs ? "border-primary/50 bg-amber-dim" : "border-border bg-secondary/40",
          )}
        >
          <Label
            htmlFor="sandbox-k"
            className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
          >
            Sandbox — not live
          </Label>
          <Input
            id="sandbox-k"
            type="number"
            min={1}
            step={10}
            value={sandboxK}
            placeholder={`${governance.liveK}`}
            onChange={(event) => onSandboxKChange(event.target.value)}
            className="mt-2 h-10 border-border bg-background text-lg tabular-nums"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            {differs
              ? "Every Z and blended rate above is recomputed against this candidate k."
              : "Enter a candidate k to see every segment move at once."}
          </p>
        </div>

        <div className="border border-border bg-secondary/40 p-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Tier boundaries
          </p>
          <ul className="mt-2 space-y-1 text-sm tabular-nums text-foreground">
            {governance.tierBoundaries.map((boundary, index) => (
              <li key={boundary} className="flex justify-between">
                <span className="text-muted-foreground">Tier {index + 1} ceiling</span>
                <span>{boundary.toFixed(2)}%</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-border">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              {["k", "Approved By", "Approved At", "Note"].map((label, i) => (
                <th
                  key={label}
                  className={cn(
                    "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                    i === 0 ? "text-right" : "text-left",
                  )}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {governance.log.map((entry) => (
              <tr
                key={`${entry.k}-${entry.approvedAt}`}
                className="border-b border-border/60 last:border-b-0"
              >
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{entry.k}</td>
                <td className="px-3 py-2.5">{entry.approvedBy}</td>
                <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                  {format(new Date(entry.approvedAt), "d MMM yyyy HH:mm")}
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{entry.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Superset runs the production calibration, and this page is the configuration and review
        surface.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            {/* `k` stays lowercase: it is the credibility parameter's SYMBOL, not a
                word, and every other surface writes it that way ("k Governance",
                "Live k", "Sandbox k"). Capitalising it here would read as a different
                quantity. The words around it are title-cased like every other header. */}
            <DialogTitle>Promote k = {validSandbox ? parsed : governance.liveK} To Live</DialogTitle>
            <DialogDescription>
              This changes the blended expected loss for every segment. A reason is required and is
              kept permanently in the log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="promote-note">Reason (Required)</Label>
            <Textarea
              id="promote-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Why is this k appropriate now?"
              className="min-h-24 border-border bg-background"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={note.trim().length === 0 || isPromoting || !differs}
              onClick={async () => {
                await onPromote(parsed, note.trim());
                setNote("");
                setOpen(false);
              }}
            >
              {isPromoting ? "Promoting…" : "Confirm Promotion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
